import * as React from "react";
import * as React44ms from "../../Util/form-utils/react-44ms";

import {
  EmailAddressFormLogic,
  EmailAddressFormLogicProps,
  maxEmailLength,
  SingleLineTextInput2023,
  useCustomerInformationQuery,
  useCustomerSession,
} from "../../Common";
import { useMyBagDataSourceIntersection } from "../providers";

import { useMyPreviouslyFilledInInfo } from "./use-my-previously-filled-in-info";
import { setLastKnownEmail } from "./email-sync-cache";

interface DeliveryDetailsEmailConfirmationProps {
  formRef: EmailAddressFormLogicProps["formRef"];
}
export const DeliveryDetailsEmailConfirmation: React.FunctionComponent<
  DeliveryDetailsEmailConfirmationProps
> = (props) => {
  const [viewModel] = React44ms.React.useForm(props.formRef);
  const inputName = React.useMemo(() => {
    return React44ms.React.Presentation.elements.nameFromRef(props.formRef);
  }, [props.formRef]);
  const { isCustomerLoggedIn } = useCustomerSession();
  useRememberMyDetails(props.formRef);

  React.useEffect(() => {
    if (!inputName) {
      return;
    }
    let input: HTMLInputElement | null = null;
    let intervalId: number | null = null;
    const handleInput = () => {
      if (!input || !input.value) {
        return;
      }
      setLastKnownEmail(input.value);
    };
    const attachToInputIfReady = () => {
      input = document.querySelector(
        `input[name="${inputName}"]`
      ) as HTMLInputElement | null;
      if (!input) {
        return false;
      }
      input.addEventListener("input", handleInput);
      input.addEventListener("change", handleInput);
      return true;
    };
    if (!attachToInputIfReady()) {
      intervalId = window.setInterval(() => {
        if (attachToInputIfReady()) {
          if (intervalId) {
            window.clearInterval(intervalId);
          }
        }
      }, 150);
    }
    return () => {
      if (input) {
        input.removeEventListener("input", handleInput);
        input.removeEventListener("change", handleInput);
      }
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [inputName, props.formRef, viewModel?.value]);

  return (
    <section className="email-confirmation">
      <h2>{`Email Confirmation`}</h2>

      <SingleLineTextInput2023
        title="Email Address"
        notice={`We will send the order confirmation here.`}
        formRef={props.formRef}
        charLength={maxEmailLength}
      />

      <EmailAddressFormLogic
        formRef={props.formRef}
        disabled={!!isCustomerLoggedIn}
      />
    </section>
  );
};

const useRememberMyDetails = (
  formRef: DeliveryDetailsEmailConfirmationProps["formRef"]
) => {
  const customerInformation = useCustomerInformationQuery();
  const { shouldMigrateDeliveryDetails, handleMigrationComplete } =
    useMyPreviouslyFilledInInfo();
  const myBagDataSource = useMyBagDataSourceIntersection();
  const handleMigration = React.useCallback(() => {
    if (
      typeof document !== "undefined" &&
      document.activeElement instanceof HTMLInputElement
    ) {
      return null;
    }
    let lastMigratedEmail = null;

    const { data: customerInformationData } = customerInformation || {};
    if (customerInformationData && customerInformationData.email) {
      setLastKnownEmail(customerInformationData.email);
      React44ms.React.Event.dispatch(
        React44ms.React.Convenient.input.onChange,
        formRef,
        customerInformationData.email
      );
      lastMigratedEmail = customerInformationData.email;
    }

    const { latest: myBagResponse } = myBagDataSource;
    if (myBagResponse) {
      if (myBagResponse.email && myBagResponse.email.length > 0) {
        setLastKnownEmail(myBagResponse.email);
        React44ms.React.Event.dispatch(
          React44ms.React.Convenient.input.onChange,
          formRef,
          myBagResponse.email
        );
        lastMigratedEmail = myBagResponse.email;
      }
    }

    return lastMigratedEmail;
  }, [
    formRef,
    customerInformation.data,
    customerInformation.data?.email,
    myBagDataSource.latest?.email,
  ]);
  React.useEffect(() => {
    if (shouldMigrateDeliveryDetails) {
      const lastMigratedEmail = handleMigration();
      handleMigrationComplete(lastMigratedEmail);
    }
  }, [shouldMigrateDeliveryDetails, handleMigration, handleMigrationComplete]);
};
